import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type TenantDetail } from '../lib/api';

/** Change the persona used for every mailbox on a tenant. Local-parts
 * + display names get regenerated from a fresh NAME_SEED; the sending
 * domain stays the same. Chains into OAuth automatically so the
 * sequencer reconnects under the new addresses.
 *
 * Only shown when the pipeline is exited (done / warning / failed)
 * so we can't fire a rename on top of an in-flight pipeline. Same
 * gating rationale as the "Re-run connector" card. */
export default function RenameMailboxesCard({ tenant }: { tenant: TenantDetail }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [seed, setSeed] = useState(tenant.name_seed || '');
  const [random, setRandom] = useState(!(tenant.name_seed || '').trim());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const hasExited =
    tenant.status === 'done' ||
    tenant.status === 'warning' ||
    tenant.status === 'failed';
  if (!hasExited) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    try {
      const payload = { name_seed: random ? null : seed.trim() || null };
      const out = await api.renameTenantMailboxes(tenant.id, payload);
      const jobId = out?.detail?.job_id ? ` (job ${out.detail.job_id.slice(0, 8)})` : '';
      setMsg(
        random
          ? `Rename queued${jobId} — mailboxes will be randomized and reconnected. The mailboxes tab will refresh as the new addresses land.`
          : `Rename queued${jobId} — every mailbox will become a "${seed.trim()}" iteration and reconnect to your sequencer.`,
      );
      qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      // Intentionally NOT collapsing on success — the operator needs
      // to see the confirmation, and the auto-heal was previously
      // eating the enqueue → the queued msg was the only signal
      // anything happened.
    } catch (e: any) {
      // Surface the API error verbatim (route may return 409 if a
      // previous job is still in flight, 502 if the CF or Graph
      // subsystem errored, etc.).
      setErr(e?.message || 'rename failed to enqueue');
      console.error('rename failed', e);
    } finally {
      setBusy(false);
    }
  }

  if (!expanded) {
    return (
      <div className="card p-4 space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="text-sm text-ink font-medium">Change mailbox personas</div>
            <div className="text-xs text-dim mt-0.5">
              Currently {tenant.name_seed ? (
                <>using seed <span className="font-mono text-ink">{tenant.name_seed}</span></>
              ) : 'using randomized names'}. Rename regenerates every mailbox
              under new local-parts and reconnects them to the sequencer.
            </div>
          </div>
          <button onClick={() => { setExpanded(true); setMsg(null); setErr(null); }} className="btn-secondary">
            Change names
          </button>
        </div>
        {msg && <div className="text-xs text-ok">{msg}</div>}
        {err && <div className="text-xs text-err">{err}</div>}
      </div>
    );
  }

  return (
    <div className="card p-5 space-y-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-dim mb-1">Rename mailboxes</div>
        <div className="text-sm text-ink">
          Every mailbox on <span className="font-mono">{tenant.target_domain}</span>{' '}
          will be renamed. The domain stays the same, only the local-parts
          and display names change. Sequencer reconnects automatically.
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="radio" name="rename-mode" checked={random}
            onChange={() => setRandom(true)}
          />
          Randomize (fresh names from a large pool)
        </label>
        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="radio" name="rename-mode" checked={!random}
            onChange={() => setRandom(false)}
          />
          Use a specific persona (or comma-separated set)
        </label>
        {!random && (
          <input
            type="text"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder="John Doe"
            className="w-full bg-white/[0.04] border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
            autoFocus
          />
        )}
        {!random && (
          <div className="text-[11px] text-dim">
            One name = all mailboxes become that persona (alex@, agalloway@, a.galloway@, …).
            Comma-separated = split evenly across the seeds.
          </div>
        )}
        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={busy || (!random && !seed.trim())}
            className="btn-primary disabled:opacity-50"
          >
            {busy ? 'Queueing…' : 'Rename & reconnect'}
          </button>
          <button
            type="button"
            onClick={() => { setExpanded(false); setErr(null); }}
            className="text-xs text-dim hover:text-ink"
          >
            Cancel
          </button>
        </div>
        {err && <div className="text-xs text-err">{err}</div>}
        {msg && <div className="text-xs text-ok">{msg}</div>}
      </form>
    </div>
  );
}
